import { Link } from "react-router";

import { Alert, Card } from "@/components/ui";
import { explorerAddress, TOKEN_SYMBOL } from "@/lib/config";
import { formatDisplay, formatTimestamp, shortenAddress } from "@/lib/format";
import type { RegistryRecordView } from "@/lib/registry/record";
import { STATUS_PRESENTATION, type StatusTone } from "@/lib/registry/status";

const TONE_CLASS: Record<StatusTone, string> = {
  good: "status-label--good",
  warn: "status-label--warn",
  bad: "status-label--bad",
  neutral: "status-label--neutral",
};

export function StatusBadge({ status }: { status: RegistryRecordView["status"] }) {
  const presentation = STATUS_PRESENTATION[status];
  return (
    <span
      className={`status-label ${TONE_CLASS[presentation.tone]}`}
    >
      {presentation.label}
    </span>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="registry-detail-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function AddressValue({ address }: { address: string }) {
  return (
    <a
      className="registry-address-link"
      href={explorerAddress(address)}
      rel="noreferrer noopener"
      target="_blank"
    >
      {shortenAddress(address, 10, 8)}
    </a>
  );
}

/**
 * Renders one registry record.
 *
 * Two things here are load-bearing rather than cosmetic:
 *
 *  - The status shown is `record.status`, which the reader already resolved
 *    through `effectiveStatus`. When it disagrees with the stored field — an
 *    expiry that lapsed with no transaction to write it down — both are shown,
 *    because hiding the discrepancy would make the page look like it is
 *    contradicting the chain.
 *  - "Boleh bertransaksi" comes from `usable`, not from the status label. For a
 *    vendor's service the parent vendor's standing counts too, and that is
 *    already folded into `usable` on-chain. Re-deriving it here would be a
 *    second implementation to keep in sync.
 */
export function RegistryRecordCard({ record }: { record: RegistryRecordView }) {
  const presentation = STATUS_PRESENTATION[record.status];
  const statusDiffers = record.storedStatus !== record.status;

  /**
   * A record's own status can read `Active` while it is still unusable — a
   * vendor service whose vendor was suspended or revoked keeps its own record
   * untouched, because that is exactly how §60 avoids stranding anyone.
   *
   * Rendering the status badge alone would then show a green "Aktif" on a
   * service nobody should transact with. `usable` is the authoritative answer
   * to "may I use this right now", so it overrides the label.
   */
  const blockedByParent = presentation.transactable && !record.usable;

  return (
    <div className="flex flex-col gap-6">
      <Card className="registry-record-card">
        <div className="registry-record-card__header">
          <div>
            <p className="terminal-label">REGISTRY ID</p>
            <p className="registry-record-card__id">
              {record.name ?? shortenAddress(record.registryId, 10, 8)}
            </p>
          </div>
          {blockedByParent ? (
            <span className={`status-label ${TONE_CLASS.warn}`}>
              Tidak tersedia
            </span>
          ) : (
            <StatusBadge status={record.status} />
          )}
        </div>

        <p className="registry-record-card__explanation">
          {blockedByParent
            ? "Catatan ini sendiri masih aktif, tetapi tidak dapat dipakai sekarang — vendor induknya sedang ditangguhkan atau sudah dicabut."
            : presentation.explanation}
        </p>

        {blockedByParent && (
          <div className="mt-4">
            <Alert tone="warn">
              Jangan lakukan transaksi baru dengan layanan ini. Statusnya sendiri belum diubah,
              jadi ia akan langsung tersedia lagi begitu vendor induknya dipulihkan — dan jalur
              penarikan dana yang sudah ada tetap harus terbuka.
            </Alert>
          </div>
        )}

        {!presentation.transactable && record.status !== "Unregistered" && (
          <div className="mt-4">
            <Alert tone="warn">
              Jangan lakukan transaksi baru dengan entitas ini. Kalau Anda punya dana atau posisi
              yang sudah berjalan, jalur penarikan dan refund tetap harus terbuka — hubungi
              dukungan kalau tidak.
            </Alert>
          </div>
        )}

        {record.status === "Unregistered" && (
          <div className="mt-4">
            <Alert tone="danger">
              ID ini tidak ada di UtilityRegistry. Siapa pun yang mengklaim sebaliknya tidak dapat
              dibuktikan lewat rantai.
            </Alert>
          </div>
        )}
      </Card>

      <Card title="Detail registry" className="registry-detail-card">
        <dl className="registry-detail-list">
          <Row label="Tipe">{record.registryType}</Row>
          {record.utilityType !== "Undefined" && <Row label="Model konsumsi">{record.utilityType}</Row>}
          <Row label="Boleh dipakai sekarang">{record.usable ? "Ya" : "Tidak"}</Row>
          <Row label="Versi">{record.version}</Row>

          {record.operator && (
            <Row label="Wallet resmi">
              <AddressValue address={record.operator} />
            </Row>
          )}
          {record.module && (
            <Row label="Module">
              <AddressValue address={record.module} />
            </Row>
          )}
          {record.permanentVault && (
            <Row label="Permanent vault">
              <AddressValue address={record.permanentVault} />
            </Row>
          )}
          {record.lockedBalanceRaw !== null && (
            <Row label="Terkunci permanen">
              {formatDisplay(BigInt(record.lockedBalanceRaw))} {TOKEN_SYMBOL}
            </Row>
          )}

          {record.parentId && (
            <Row label="Vendor induk">
              <Link
                className="text-brand underline decoration-dotted underline-offset-4 hover:decoration-solid"
                to={`/vendor/${record.parentId}`}
              >
                {shortenAddress(record.parentId, 10, 8)}
              </Link>
            </Row>
          )}

          <Row label="Terdaftar">{formatTimestamp(record.registeredAt)}</Row>
          {record.activatedAt > 0 && <Row label="Aktif sejak">{formatTimestamp(record.activatedAt)}</Row>}
          {record.expiresAt > 0 && <Row label="Berlaku sampai">{formatTimestamp(record.expiresAt)}</Row>}

          {record.certificateTokenId && (
            <Row label="Sertifikat">NFT #{record.certificateTokenId}</Row>
          )}

          <Row label="Metadata hash">
            <span className="font-mono text-xs">{record.metadataHash}</span>
          </Row>
        </dl>

        {statusDiffers && (
          <div className="mt-4">
            <Alert tone="info">
              Status tersimpan di rantai masih {record.storedStatus}, tetapi masa berlakunya sudah
              lewat sehingga status efektifnya {record.status}. Perubahan ini tidak memerlukan
              transaksi — yang berlaku adalah status efektif.
            </Alert>
          </div>
        )}
      </Card>

      <Card title="Cara memverifikasi sendiri" className="registry-self-check">
        <p className="text-sm leading-relaxed text-ink-muted">
          Halaman ini membaca <code className="font-mono text-xs">effectiveStatus()</code> dari
          UtilityRegistry di rantai, bukan basis data. Anda dapat memanggil fungsi yang sama
          langsung di BscScan dengan registry ID{" "}
          <span className="font-mono text-xs">{record.registryId}</span> dan membandingkan
          hasilnya.
        </p>
        {record.certificateTokenId && (
          <p className="mt-3 text-sm leading-relaxed text-ink-muted">
            Sertifikat NFT <strong>bukan</strong> sumber kebenaran. Sertifikat tetap berada di
            wallet pemegangnya walaupun statusnya sudah dicabut — yang menentukan adalah registry.
          </p>
        )}
      </Card>
    </div>
  );
}
